import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { TabContext, TabPanel } from "@mui/lab";
import { Box, Button, Collapse, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useState } from "react";
import { ReactElement } from "react-markdown/lib/react-markdown";
import { ReactComponent as ConnectionIcon } from "../../../assets/icons/connection.svg";
import { ReactComponent as Affinity } from "../../../assets/logo/affinity-logo.svg";
import { ReactComponent as Airtable } from "../../../assets/logo/airtable.svg";
import AttioImg, { ReactComponent as Attio } from "../../../assets/logo/attio.svg";
import { ReactComponent as HubSpot } from "../../../assets/logo/hubspot.svg";
import useIntegration from '../../../hooks/useIntegration';
import { scrollbarStyle } from '../../../shared/dashboard';
import theme from "../../../theme";
import { integrationStatus } from '../../../types/integrations';

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        minWidth: 330,
        width: '100%',
        height: 'fit-content',
        minHeight: 80,
        padding: '12px 16px',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['400']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2)',
        transition: 'all 300ms ease-in-out',
    },
    cardIcon: {
        position: 'relative',
        width: 'fit-content',
        height: 'fit-content',
        padding: 16,
        borderRadius: 20,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['400']}`,
        "&:hover": {
            boxShadow: '0px 5px 8px 0px rgba(16, 24, 40,0.14)',
        },
        "& > svg": {
            width: 60,
            height: 60
        },
    },
    selectedIcon: {
        position: 'relative',
        width: 'fit-content',
        height: 'fit-content',
        padding: 16,
        borderRadius: 20,
        backgroundColor: '#fff',
        border: `1.5px solid ${theme.palette.primary.light}`,
        boxShadow: '0px 5px 8px 0px rgba(16, 24, 40,0.14)',
        "& > svg": {
            width: 40,
            height: 40
        },
    },
    mainIcon: {
        width: 24,
        height: 24,
        fill: '#666666',
        transform: 'rotate(270deg)',
    },
    selectionIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.neutral['400'],
    },
    title: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    subtitle: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: theme.colors.neutral['500'],
    },
    setup: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        color: theme.colors.neutral['600'],
    },
    instructions: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        color: theme.colors.neutral['500'],
    },
    label: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: theme.colors.neutral['500'],
    },
    backButton: {
        padding: 'unset',
        minWidth: 90,
        borderRadius: 32,
        color: theme.palette.primary.main,
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        textTransform: 'none',
        "&:hover": {
            backgroundColor: 'unset',
        },
    },
    link: {
        fontFamily: 'Inter',
        cursor: 'pointer',
        color: theme.palette.primary.light,
        textDecoration: 'underline',
    },
    leftDivider: {
        height: 500,
        borderWidth: '1.5px',
        borderColor: theme.colors.primary['300'],
        transform: 'translate(1px,20px)'
    },
    leftBulletIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.primary['300'],
        backgroundColor: '#fff',
        transform: 'translateX(-12px)',
    },
    longLine: {
        width: '100%',
        borderBottom: `3px solid ${theme.colors.primary['200']}`,
        marginLeft: '-16px',
        marginRight: '-12px',
        zIndex: 1,
    },
    rightArrowIcon : {
        width: 32,
        height: 32,
        fill: theme.colors.primary['300'],
        zIndex: 1,
    },
    connectBtn: {
        width: 160,
        height: 36,
        borderRadius: 40,
        backgroundColor: theme.colors.primary['600'],
        color: 'white',
        textTransform: 'none',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 600,
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}));

const AffinityInstructions: React.FC<{}> = () => {
    const classes = useStyles();

    return (<>
        <Typography className={classes.instructions}>
            <span>{'1. Select Notissia as Trigger'}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'a. Select "New Dashboard"'}</span><br/>
            <span>{`2. Select Affinity as Action`}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'a. Step 1: Find an organization '}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'b. Step 2: Find a list entry'}</span><br/>
            <span style={{ paddingLeft: '32px' }}>{'i. Use "list organization id"'}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'c. Step 3: Update an entity'}</span><br/>
            <span style={{ paddingLeft: '32px' }}>{'i. Use "list entry composite id"'}</span><br/>
            <span style={{ paddingLeft: '32px' }}>{'ii. Map field from Notissia'}</span><br/>
        </Typography>
    </>);
};

const AirtableInstructions: React.FC<{}> = () => {
    const classes = useStyles();

    return (<>
        <Typography className={classes.instructions}>
            <span>{'1. Select Notissia as Trigger'}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'a. Select "New Dashboard"'}</span><br/>
            <span>{`2. Select Airtable as Action`}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'a. Step 1: Create Record '}</span><br/>
            <span style={{ paddingLeft: '32px' }}>{'i. Select "Base"'}</span><br/>
            <span style={{ paddingLeft: '32px' }}>{'ii. Select "Table"'}</span><br/>
            <span style={{ paddingLeft: '32px' }}>{'iii. Map field from Notissia'}</span><br/>
        </Typography>
    </>);
};

const HubspotInstructions: React.FC<{}> = () => {
    const classes = useStyles();

    return (<>
        <Typography className={classes.instructions}>
            <span>{'1. Select Notissia as Trigger'}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'a. Select "New Dashboard"'}</span><br/>
            <span>{`2. Select HubSpot as Action`}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'a. Step 1: Find Company '}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'b. Step 2: Create Deal'}</span><br/>
        </Typography>
    </>);
};

const AttioInstructions: React.FC<{}> = () => {
    const classes = useStyles();

    return (<>
        <Typography className={classes.instructions}>
            <span>{'1. Select Notissia as Trigger'}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'a. Select "New Dashboard"'}</span><br/>
            <span>{`2. Select Attio as Action`}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'a. Step 1: Create or Update Record'}</span><br/>
            <span style={{ paddingLeft: '16px' }}>{'b. Step 2: Create List Entry'}</span><br/>
        </Typography>
    </>);
};

const ConnectToCRM: React.FC<{}> = () => {
    const classes = useStyles();
    const { integrations, authorizeIntegration } = useIntegration();

    const [layout, setLayout] = useState<'selection'|'instruction'>('selection');
    const [isExpanded, setExpanded] = useState<boolean>(false);
    const [SelectedIcon, setSelectedIcon] = useState<ReactElement|null>(null);
    const [selected, setSelected] = useState<string|null>(null);

    const zapier = useMemo(() => ({
        isConnected: integrations.some(x => x.integration === 'Zapier' as any && x.status === integrationStatus.CONNECTED),
        isError: integrations.some(x => x.integration === 'Zapier' as any && x.status === integrationStatus.ERROR),
        connect: () => { authorizeIntegration({ integrationToConnect: 'Zapier' as any }) },
    }), [integrations, authorizeIntegration]);

    const handleSelect = useCallback((value: 'selection' | 'instruction', Icon: ReactElement, label: string) => {
        setLayout(value);
        setSelected(label);
        setSelectedIcon(Icon);
    }, []);

    return (<>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" width="100%" pr={2}>
            <Stack direction="row" alignItems="center" width="100%" height="100%">
                <Divider className={classes.leftDivider} />
                <RadioButtonUncheckedIcon className={classes.leftBulletIcon} />
                <Box className={classes.longLine} />
                <PlayArrowIcon className={classes.rightArrowIcon} />
            </Stack>
            <Stack className={classes.card} spacing={1} alignItems="flex-start">
                <Stack direction="row" spacing={1} alignItems="center" width="100%">
                    <ConnectionIcon className={classes.mainIcon} />
                    <Stack spacing="4px" alignItems="flex-start" width="100%">
                        <Typography className={classes.title}>
                            {'Connect to your CRM'}
                        </Typography>
                    </Stack>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" pl={4}>
                    <Typography className={classes.subtitle}>
                        {'Select your CRM'}
                    </Typography>
                    {layout === 'instruction' && (
                        <Button variant="text"
                            className={classes.backButton}
                            startIcon={<ArrowBackIcon />}
                            onClick={() => setLayout('selection')}>
                            {'Back'}
                        </Button>
                    )}
                </Stack>
                <TabContext value={layout}>
                    <TabPanel value="selection" sx={{ width: '100%', height: '100%', p: 'unset'}}>
                        <Stack width="100%" height="100%" pl={4}>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gridTemplateRows='1fr 1fr' gap="16px 24px" width="fit-content">
                                <Tooltip placement="top-start" title={
                                        <Typography component="span" fontSize="0.9rem" fontWeight={600}>
                                            {`Affinity`}
                                        </Typography>
                                    } componentsProps={{tooltip:{sx:{
                                        display: 'flex', alignItems: 'center', backgroundColor: 'rgba(150, 150, 150, 0.9)',
                                        transform: 'translate(4px, 12px) !important', height: '28px', padding: 2,
                                    }}}}>
                                    <IconButton className={classes.cardIcon}
                                        sx={{ paddingTop: '12px !important', paddingBottom: '20px !important' }}
                                        onClick={() => handleSelect('instruction', <Affinity />, 'Affinity')}
                                        disableRipple>
                                        <Affinity />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top-start" title={
                                        <Typography component="span" fontSize="0.9rem" fontWeight={600}>
                                            {`Airtable`}
                                        </Typography>
                                    } componentsProps={{tooltip:{sx:{
                                        display: 'flex', alignItems: 'center', backgroundColor: 'rgba(150, 150, 150, 0.9)',
                                        transform: 'translate(4px, 12px) !important', height: '28px', padding: 2,
                                    }}}}>
                                    <IconButton className={classes.cardIcon}
                                        onClick={() => handleSelect('instruction', <Airtable />, 'Airtable')}
                                        disableRipple>
                                        <Airtable />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top-start" title={
                                        <Typography component="span" fontSize="0.9rem" fontWeight={600}>
                                            {`HubSpot`}
                                        </Typography>
                                    } componentsProps={{tooltip:{sx:{
                                        display: 'flex', alignItems: 'center', backgroundColor: 'rgba(150, 150, 150, 0.9)',
                                        transform: 'translate(0, 12px) !important', height: '28px', padding: 2,
                                    }}}}>
                                    <IconButton className={classes.cardIcon}
                                        onClick={() => handleSelect('instruction', <HubSpot />, 'HubSpot')}
                                        disableRipple>
                                        <HubSpot />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top-start" title={
                                        <Typography component="span" fontSize="0.9rem" fontWeight={600}>
                                            {`Attio`}
                                        </Typography>
                                    } componentsProps={{tooltip:{sx:{
                                        display: 'flex', alignItems: 'center', backgroundColor: 'rgba(150, 150, 150, 0.9)',
                                        transform: 'translate(12px, 12px) !important', height: '28px', padding: 2,
                                    }}}}>
                                    <IconButton className={classes.cardIcon}
                                        onClick={() => handleSelect('instruction', <Attio />, 'Attio')}
                                        disableRipple>
                                        <img alt="Attio" src={AttioImg} style={{ width: 60, height: 60 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Stack>
                        <Stack alignItems="center" mt={3}>
                            <Typography className={classes.subtitle} fontWeight="600">
                                {'Don\'t see your CRM?'}
                            </Typography>
                            <Typography className={classes.subtitle}>
                                {'Let us know: '}
                                <Typography component="span" className={classes.link} display="inline"
                                    onClick={() => window.open(`mailto: support@notissia.com`, '_self', 'noopener, noreferrer')}>
                                    {'support@notissia.com'}
                                </Typography>
                            </Typography>
                        </Stack>
                    </TabPanel>
                    <TabPanel value="instruction" sx={{ width: '100%', height: '100%', p: 'unset '}}>
                         <Stack width="100%" height="100%" pl={4}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                                <IconButton className={classes.selectedIcon} disabled>
                                    {SelectedIcon}
                                </IconButton>
                                <Button variant="contained"
                                    className={classes.connectBtn}
                                    onClick={!zapier.isConnected ? zapier.connect : undefined}
                                    disabled={zapier.isConnected || zapier.isError}>
                                    {zapier.isConnected ? 'Already connected' : zapier.isError ? 'Connection error' :'Connect in Zapier'}
                                </Button>
                            </Stack>
                         </Stack>
                         <Stack width="100%" height="100%" p={1}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" p="4px">
                                <Typography className={classes.setup} fontWeight="600">
                                    {'Set up instructions for Zapier'}
                                </Typography>
                                <Button variant="text"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setExpanded((prev) => !prev);
                                    }}
                                    sx={{ minWidth: 'unset', padding: 'unset' }}>
                                    <ExpandMoreIcon sx={{ fill: '#666666', transform: `rotate(${isExpanded ? 180 : 0}deg)` }} />
                                </Button>
                            </Stack>
                            <Box width="100%" height="fit-content">
                                <Collapse in={isExpanded} collapsedSize={0}>
                                    <Divider color={theme.colors.neutral['600']} sx={{ width: '100%', marginBottom: 2 }} />
                                    <Box width="100%" height="fit-content" maxHeight="calc(100vh - 700px)"
                                        sx={{ overflowY: 'auto', ...scrollbarStyle }}>
                                        {selected === 'Affinity' && <AffinityInstructions />}
                                        {selected === 'Airtable' && <AirtableInstructions />}
                                        {selected === 'HubSpot' && <HubspotInstructions />}
                                        {selected === 'Attio' && <AttioInstructions />}
                                    </Box>
                                    <Stack alignItems="center" mt={2}>
                                        <Typography className={classes.subtitle} fontWeight="600">
                                            {'Need help?'}
                                        </Typography>
                                        <Typography className={classes.subtitle}>
                                            {'Contact us: '}
                                            <Typography component="span" className={classes.link} display="inline"
                                                onClick={() => window.open(`mailto: support@notissia.com`, '_self', 'noopener, noreferrer')}>
                                                {'support@notissia.com'}
                                            </Typography>
                                        </Typography>
                                    </Stack>
                                </Collapse>
                            </Box>
                         </Stack>
                    </TabPanel>
                </TabContext>
            </Stack>
        </Stack>
    </>);
}

export default ConnectToCRM;
